import * as signalR from '@microsoft/signalr';

type SignalREventHandlers = {
  onReservationCreated?: (data: { reservaId: number; mesaId?: number; data: string; numeroPessoas: number; nomeCliente?: string; estado: string }) => void;
  onReservationUpdated?: (data: { reservaId: number; mesaId?: number; data: string; numeroPessoas: number; nomeCliente?: string }) => void;
  onReservationCancelled?: (data: { reservaId: number; mesaId?: number }) => void;
  onReservationConfirmed?: (data: { reservaId: number; mesaId?: number; historicoId: number }) => void;
  onHistoryCreated?: (data: { historicoId: number; mesaId: number; data: string; numeroPessoas: number; origem: number }) => void;
  onHistoryDeleted?: (data: { historicoId: number; mesaId: number }) => void;
  onTableChanged?: (data: { mesaId: number; numero: number; capacidade: number; podeReservar: boolean }) => void;
  onSyncRequested?: (data: Date) => void;
  onDataChanged?: () => void;
  onConnectionChange?: (connected: boolean) => void;
};

class SignalRService {
  private connection: signalR.HubConnection | null = null;
  private handlersList: SignalREventHandlers[] = [];
  private tokenRefreshCallback: (() => Promise<void>) | null = null;
  private handlersRegistered = false;
  private notifyTimer: number | null = null;

  setTokenRefreshCallback(callback: () => Promise<void>) {
    this.tokenRefreshCallback = callback;
  }

  async start(handlers: SignalREventHandlers): Promise<void> {
    // Add handler to list
    this.handlersList.push(handlers);

    // If connection exists and is connected, just notify the new handler
    if (this.connection) {
      const state = this.connection.state;
      if (state === signalR.HubConnectionState.Connected) {
        handlers.onConnectionChange?.(true);
        return;
      }
      if (state === signalR.HubConnectionState.Connecting || state === signalR.HubConnectionState.Reconnecting) {
        return;
      }
      // If disconnected, will recreate below
    }

    this.connection = new signalR.HubConnectionBuilder()
      .withUrl('/hubs/map', { withCredentials: true })
      .withAutomaticReconnect([0, 2000, 10000, 30000])
      .configureLogging(signalR.LogLevel.Information)
      .build();

    this.registerHandlers();
    this.setupConnectionEvents();

    try {
      await this.connection.start();
      if (import.meta.env.DEV) console.log('SignalR connected');
      this.handlersList.forEach(h => h.onConnectionChange?.(true));
    } catch (error) {
      if (import.meta.env.DEV) console.error('SignalR connection failed:', error);
      this.handlersList.forEach(h => h.onConnectionChange?.(false));
      throw error;
    }
  }

  private forEachHandler(cb: (h: SignalREventHandlers) => void) {
    // copia defensiva: handlers podem remover-se durante notificação
    [...this.handlersList].forEach(cb);
  }

  private notifyDataChanged() {
    // Debounce 250ms: rajadas (ex. confirmar reserva gera 2 eventos) disparam 1 reload
    if (this.notifyTimer) window.clearTimeout(this.notifyTimer);
    this.notifyTimer = window.setTimeout(() => {
      this.notifyTimer = null;
      this.forEachHandler(h => h.onDataChanged?.());
    }, 250);
  }

  private registerHandlers(): void {
    if (!this.connection || this.handlersRegistered) return;

    this.connection.on('ReservationCreated', (data) => {
      this.forEachHandler(h => h.onReservationCreated?.(data));
      this.notifyDataChanged();
    });

    this.connection.on('ReservationUpdated', (data) => {
      this.forEachHandler(h => h.onReservationUpdated?.(data));
      this.notifyDataChanged();
    });

    this.connection.on('ReservationCancelled', (data) => {
      this.forEachHandler(h => h.onReservationCancelled?.(data));
      this.notifyDataChanged();
    });

    this.connection.on('ReservationConfirmed', (data) => {
      this.forEachHandler(h => h.onReservationConfirmed?.(data));
      this.notifyDataChanged();
    });

    this.connection.on('HistoryCreated', (data) => {
      this.forEachHandler(h => h.onHistoryCreated?.(data));
      this.notifyDataChanged();
    });

    this.connection.on('HistoryDeleted', (data) => {
      this.forEachHandler(h => h.onHistoryDeleted?.(data));
      this.notifyDataChanged();
    });

    this.connection.on('TableChanged', (data) => {
      this.forEachHandler(h => h.onTableChanged?.(data));
      this.notifyDataChanged();
    });

    this.connection.on('SyncRequested', (data) => {
      this.forEachHandler(h => h.onSyncRequested?.(data));
      this.notifyDataChanged();
    });

    this.handlersRegistered = true;
  }

  private setupConnectionEvents(): void {
    if (!this.connection) return;

    this.connection.onreconnecting(() => {
      if (import.meta.env.DEV) console.log('SignalR reconnecting...');
      this.forEachHandler(h => h.onConnectionChange?.(false));
    });

    this.connection.onreconnected(async (connectionId) => {
      if (import.meta.env.DEV) console.log('SignalR reconnected:', connectionId);
      // Revalida sessão (cookie pode ter expirado durante offline)
      try {
        await this.tokenRefreshCallback?.();
      } catch {
        // 401 já limpa AuthContext via evento; segue para sync na mesma
      }
      this.forEachHandler(h => h.onConnectionChange?.(true));
      this.requestSync(new Date());
    });

    this.connection.onclose(() => {
      if (import.meta.env.DEV) console.log('SignalR connection closed');
      this.forEachHandler(h => h.onConnectionChange?.(false));
    });
  }

  async requestSync(data: Date): Promise<void> {
    if (this.connection?.state === signalR.HubConnectionState.Connected) {
      try {
        await this.connection.invoke('RequestSync', data);
      } catch {
        // silencioso: próximo evento faz re-sync
      }
    }
  }

  async stop(handlers?: SignalREventHandlers): Promise<void> {
    if (handlers) {
      this.handlersList = this.handlersList.filter(h => h !== handlers);
    }
    // Sem handlers => não remove nada (evita pop() remover handler errado).
    // A ligação só fecha via stopAll (logout) ou quando a lista esvazia.
    if (this.handlersList.length === 0 && this.connection) {
      if (this.notifyTimer) {
        window.clearTimeout(this.notifyTimer);
        this.notifyTimer = null;
      }
      try { await this.connection.stop(); } catch { /* ignore */ }
      this.connection = null;
      this.handlersRegistered = false;
    }
  }

  // usado para limpar tudo (ex: logout)
  async stopAll(): Promise<void> {
    this.handlersList = [];
    if (this.notifyTimer) {
      window.clearTimeout(this.notifyTimer);
      this.notifyTimer = null;
    }
    if (this.connection) {
      try { await this.connection.stop(); } catch { /* ignore */ }
      this.connection = null;
      this.handlersRegistered = false;
    }
  }

  get state(): signalR.HubConnectionState | null {
    return this.connection?.state ?? null;
  }

  isConnected(): boolean {
    return this.connection?.state === signalR.HubConnectionState.Connected;
  }
}

export const signalRService = new SignalRService();
